import {
  DestroyRef,
  Directive,
  ElementRef,
  InjectionToken,
  NgZone,
  computed,
  effect,
  inject,
  input,
  numberAttribute,
} from '@angular/core';
import { prefersReducedMotion } from '../services/reduced-motion';

// Test escape hatch: provide `true` to render the final value synchronously.
export const COUNT_UP_DISABLED = new InjectionToken<boolean>('COUNT_UP_DISABLED', {
  factory: () => false,
});

const DURATION_MS = 700;

// Counts the element's text up from 0 to [appCountUp] on mount (V3 call sites only — V2
// never attaches it). Mount-only like the chart animations: later value changes snap
// without re-animating. Reduced motion renders the final value immediately.
@Directive({
  selector: '[appCountUp]',
  standalone: true,
  // Tabular digits so the text doesn't wobble while counting.
  host: { style: 'font-variant-numeric: tabular-nums' },
})
export class CountUp {
  readonly value = input.required<number>({ alias: 'appCountUp' });
  // Fraction digits of the rendered value (de-DE, thousands grouping included — matches the
  // priceFormat/forex pipes' output).
  readonly decimals = input(0, { transform: numberAttribute });

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly disabled = inject(COUNT_UP_DISABLED);
  private frame = 0;
  private mounted = false;

  private readonly formatter = computed(
    () =>
      new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: this.decimals(),
        maximumFractionDigits: this.decimals(),
      }),
  );

  constructor() {
    effect(() => {
      const target = this.value();
      const skipAnimation = this.mounted || this.disabled || prefersReducedMotion();
      if (skipAnimation) {
        cancelAnimationFrame(this.frame);
        this.render(target);
      } else {
        this.animate(target);
      }
      this.mounted = true;
    });
    inject(DestroyRef).onDestroy(() => cancelAnimationFrame(this.frame));
  }

  private animate(target: number) {
    this.zone.runOutsideAngular(() => {
      let start: number | null = null;
      const step = (now: number) => {
        start ??= now;
        const t = Math.min((now - start) / DURATION_MS, 1);
        this.render(target * (1 - Math.pow(1 - t, 3))); // ease-out cubic
        if (t < 1) this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    });
  }

  private render(value: number) {
    const rounded = Number(value.toFixed(this.decimals()));
    // `|| 0` normalizes -0 — never show a "-0" frame.
    this.el.nativeElement.textContent = this.formatter().format(rounded || 0);
  }
}
